import { useState, useEffect, useCallback } from 'react';
import { apiFetch, getAccessToken } from '../lib/api.js';
import { useToast } from '../components/Toast.js';

interface Integration {
  id: string;
  type: string;
  status: string;
  lastSuccessAt: string | null;
  lastErrorAt: string | null;
  lastError: string | null;
  createdAt: string;
}

const ADAPTER_INFO: Record<string, { name: string; description: string; color: string }> = {
  zapier: { name: 'Zapier', description: 'Connect to any CRM via Zapier webhook', color: 'bg-orange-500' },
  ghl: { name: 'Go High Level', description: 'Direct integration with GHL contacts', color: 'bg-blue-600' },
  spark: { name: 'Spark Membership', description: 'Push leads to Spark prospects', color: 'bg-purple-600' },
  keap: { name: 'Keap', description: 'Infusionsoft/Keap with OAuth2', color: 'bg-green-600' },
  zenplanner: { name: 'Zen Planner', description: 'Zen Planner prospect integration', color: 'bg-teal-600' },
};

export function IntegrationsPage() {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [orgId, setOrgId] = useState<string | null>(null);
  const { toast } = useToast();

  // Decode org_id from JWT
  useEffect(() => {
    const token = getAccessToken();
    if (token) {
      try {
        const parts = token.split('.');
        const payload = JSON.parse(atob(parts[1] ?? ''));
        setOrgId(payload.org_id);
      } catch { /* ignore */ }
    }
  }, []);

  const fetchIntegrations = useCallback(async () => {
    if (!orgId) return;
    setIsLoading(true);
    try {
      const data = await apiFetch<{ integrations: Integration[] }>(`/orgs/${orgId}/integrations`);
      setIntegrations(data.integrations);
    } catch (err) {
      console.error('Failed to fetch integrations:', err);
    } finally {
      setIsLoading(false);
    }
  }, [orgId]);

  useEffect(() => { fetchIntegrations(); }, [fetchIntegrations]);

  const handleAdd = async (type: string, config: Record<string, string>) => {
    if (!orgId) return;
    try {
      await apiFetch(`/orgs/${orgId}/integrations`, {
        method: 'POST',
        body: JSON.stringify({
          type,
          config: type === 'zapier' ? { webhook_url: config['webhook_url'] } : {},
          credentials: type !== 'zapier' ? { api_key: config['api_key'] } : {},
        }),
      });
      toast('success', `${ADAPTER_INFO[type]?.name ?? type} integration added`);
      setShowAddForm(false);
      fetchIntegrations();
    } catch {
      toast('error', 'Failed to add integration');
    }
  };

  const handleRemove = async (id: string) => {
    if (!orgId) return;
    try {
      await apiFetch(`/orgs/${orgId}/integrations/${id}`, { method: 'DELETE' });
      toast('info', 'Integration removed');
      fetchIntegrations();
    } catch {
      toast('error', 'Failed to remove integration');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Integrations</h2>
          <p className="text-sm text-gray-500">Connect your CRM to receive leads automatically</p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
        >
          + Add
        </button>
      </div>

      {showAddForm && <AddIntegrationForm onAdd={handleAdd} onCancel={() => setShowAddForm(false)} />}

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map(i => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-200 rounded-lg" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-32 mb-2" />
                  <div className="h-3 bg-gray-100 rounded w-48" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : integrations.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
          </div>
          <h3 className="text-gray-900 font-medium">No integrations yet</h3>
          <p className="text-gray-500 text-sm mt-1">Add a Zapier webhook or direct CRM connection.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {integrations.map(int => {
            const info = ADAPTER_INFO[int.type] ?? { name: int.type, description: '', color: 'bg-gray-500' };
            return (
              <div key={int.id} className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${info.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <span className="text-white text-xs font-bold">{info.name.slice(0, 2).toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 text-sm">{info.name}</p>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                        int.status === 'active' ? 'bg-green-100 text-green-700' :
                        int.status === 'error' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {int.status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {int.lastSuccessAt ? `Last sync: ${new Date(int.lastSuccessAt).toLocaleDateString()}` :
                       int.lastError ? `Error: ${int.lastError}` : 'No syncs yet'}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemove(int.id)}
                    className="text-gray-400 hover:text-red-500 transition-colors p-1"
                    title="Remove"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AddIntegrationForm({ onAdd, onCancel }: {
  onAdd: (type: string, config: Record<string, string>) => void;
  onCancel: () => void;
}) {
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [config, setConfig] = useState<Record<string, string>>({});

  const types = Object.entries(ADAPTER_INFO);

  if (!selectedType) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <h3 className="font-medium text-gray-900 text-sm mb-3">Choose integration type</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {types.map(([key, info]) => (
            <button
              key={key}
              onClick={() => setSelectedType(key)}
              className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-all text-left"
            >
              <div className={`w-8 h-8 ${info.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                <span className="text-white text-xs font-bold">{info.name.slice(0, 2).toUpperCase()}</span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{info.name}</p>
                <p className="text-xs text-gray-500">{info.description}</p>
              </div>
            </button>
          ))}
        </div>
        <button onClick={onCancel} className="mt-3 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
      </div>
    );
  }

  const info = ADAPTER_INFO[selectedType]!;
  const isZapier = selectedType === 'zapier';

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4">
      <div className="flex items-center gap-2 mb-4">
        <div className={`w-8 h-8 ${info.color} rounded-lg flex items-center justify-center`}>
          <span className="text-white text-xs font-bold">{info.name.slice(0, 2).toUpperCase()}</span>
        </div>
        <h3 className="font-medium text-gray-900 text-sm">Add {info.name}</h3>
      </div>

      <div className="space-y-3">
        {isZapier ? (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Webhook URL</label>
            <input
              type="url"
              placeholder="https://hooks.zapier.com/hooks/catch/..."
              value={config['webhook_url'] ?? ''}
              onChange={e => setConfig({ ...config, webhook_url: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        ) : (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">API Key</label>
            <input
              type="password"
              placeholder="Enter your API key"
              value={config['api_key'] ?? ''}
              onChange={e => setConfig({ ...config, api_key: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        )}
      </div>

      <div className="flex gap-2 mt-4">
        <button
          onClick={() => onAdd(selectedType, config)}
          disabled={isZapier ? !config['webhook_url'] : !config['api_key']}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors"
        >
          Connect
        </button>
        <button onClick={() => { setSelectedType(null); setConfig({}); }} className="text-sm text-gray-500 hover:text-gray-700 px-3 py-2">
          Back
        </button>
        <button onClick={onCancel} className="text-sm text-gray-500 hover:text-gray-700 px-3 py-2">Cancel</button>
      </div>
    </div>
  );
}
